/**
 * ============================================================================
 * AppSidebar — 좌측 사이드바 메뉴 (antd Menu inline 모드)
 * ============================================================================
 *
 * 【역할】
 *   - useMenuStore.tree 를 antd Menu items 로 변환 후 렌더 (재귀 children 포함).
 *   - 메뉴 클릭 시 navigate(path) + useTabStore.openTab 동시 처리 (탭 자동 등록).
 *   - 백엔드 미연결 시 DEFAULT_MENU 더미 트리 폴백.
 *   - PrimeVue 'pi pi-xxx' 아이콘 이름을 antd 아이콘 컴포넌트로 매핑 (포팅 잔재 흡수).
 *
 * 【주요 기능】
 *   - iconFor(raw): 'pi pi-shopping-cart' / 'pi-shopping-cart' / 'shopping-cart' 모두 정규화.
 *   - toItems(nodes): MenuNode[] → antd MenuProps['items'] 재귀 변환.
 *   - openKeys 는 useMenuStore 관리 (사이드바 toggle expand/collapse 상태 영속).
 *   - selectedKeys: location.pathname 으로 현재 라우트와 메뉴 highlight 동기화.
 *
 * 【렌더 흐름 (ASCII)】
 *
 *      RootLayout
 *           │
 *           ▼
 *      <Sider collapsed?>
 *           │
 *           ▼
 *      AppSidebar  ──fetch──▶ useMenuStore.tree
 *           │                       │ (없으면 DEFAULT_MENU 폴백)
 *           ▼                       ▼
 *      toItems(tree) ──▶  antd <Menu inline items={...}/>
 *           │
 *      클릭 ▼
 *      onClick(info)
 *           │
 *           ├─▶ navigate(info.key)        // path 이동
 *           └─▶ openTab({key,label,path}) // 탭바 자동 등록
 *
 * 【Collapsed 상태】 RootLayout 의 Sider collapsed 가 true 면 폭 64px, 텍스트 숨김 +
 *   아이콘만 표시 (antd Menu inline 모드 자체 처리).
 *
 * 【사용처】 RootLayout layoutMode='side' 분기의 <Sider> 내부.
 * 【포팅 원본】 react-spring-fw AppSidebar.vue (Composition API → Hooks).
 * 【의존성】 antd Menu, react-router-dom, @/store/menuStore, @/store/tabStore, @/store/themeStore.
 */
import { useMemo, type ReactNode } from 'react'
import { Menu, MenuProps } from 'antd'
import {
  AppstoreOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserOutlined,
  InboxOutlined,
  UnorderedListOutlined,
  FileAddOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  HomeOutlined,
  SettingOutlined,
  TagsOutlined,
  ClusterOutlined,
  CommentOutlined,
  TranslationOutlined,
  HistoryOutlined,
  IdcardOutlined,
  DollarOutlined,
  BgColorsOutlined,
  EditOutlined,
  PlayCircleOutlined,
  TableOutlined,
  LayoutOutlined,
  ExpandOutlined,
  BarsOutlined,
  BellOutlined,
  PictureOutlined,
  CloudUploadOutlined,
  StarOutlined,
  ShareAltOutlined,
  AlignLeftOutlined,
  NumberOutlined,
  AppstoreAddOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  FileExcelOutlined,
  SoundOutlined,
  CheckSquareOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  CodepenOutlined,
  CopyOutlined,
  DotChartOutlined,
  ProfileOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMenuStore, type MenuNode } from '@/store/menuStore'
import { useTabStore } from '@/store/tabStore'
import { useThemeStore } from '@/store/themeStore'

/** PrimeVue 'pi pi-xxx' / 'pi-xxx' 아이콘 이름을 antd 아이콘 컴포넌트로 매핑 */
const ICON_MAP: Record<string, ReactNode> = {
  'shopping-cart': <ShoppingCartOutlined />,
  'shopping-bag': <ShoppingOutlined />,
  users: <UserOutlined />,
  user: <UserOutlined />,
  'id-card': <IdcardOutlined />,
  box: <InboxOutlined />,
  cube: <CodepenOutlined />,
  list: <UnorderedListOutlined />,
  'list-check': <CheckSquareOutlined />,
  clipboard: <ProfileOutlined />,
  'file-edit': <EditOutlined />,
  'file-plus': <FileAddOutlined />,
  'pen-to-square': <EditOutlined />,
  'chart-bar': <BarChartOutlined />,
  'chart-line': <LineChartOutlined />,
  'chart-pie': <PieChartOutlined />,
  'chart-scatter': <DotChartOutlined />,
  dollar: <DollarOutlined />,
  home: <HomeOutlined />,
  cog: <SettingOutlined />,
  tags: <TagsOutlined />,
  sitemap: <ClusterOutlined />,
  comment: <CommentOutlined />,
  language: <TranslationOutlined />,
  history: <HistoryOutlined />,
  palette: <BgColorsOutlined />,
  sparkles: <StarOutlined />,
  pencil: <EditOutlined />,
  play: <PlayCircleOutlined />,
  stop: <PlayCircleOutlined />,
  table: <TableOutlined />,
  'objects-column': <LayoutOutlined />,
  'window-maximize': <ExpandOutlined />,
  bars: <BarsOutlined />,
  bell: <BellOutlined />,
  images: <PictureOutlined />,
  image: <PictureOutlined />,
  'cloud-upload': <CloudUploadOutlined />,
  upload: <CloudUploadOutlined />,
  'share-alt': <ShareAltOutlined />,
  'align-left': <AlignLeftOutlined />,
  hashtag: <NumberOutlined />,
  code: <NumberOutlined />,
  'th-large': <AppstoreAddOutlined />,
  calendar: <CalendarOutlined />,
  'file-pdf': <FilePdfOutlined />,
  print: <PrinterOutlined />,
  qrcode: <QrcodeOutlined />,
  'file-excel': <FileExcelOutlined />,
  megaphone: <SoundOutlined />,
  server: <DatabaseOutlined />,
  shield: <SafetyOutlined />,
  expand: <ExpandOutlined />,
  clone: <CopyOutlined />,
  map: <EnvironmentOutlined />,
  'map-marker': <EnvironmentOutlined />,
  clock: <ClockCircleOutlined />
}

/**
 * 아이콘 raw 문자열을 antd 아이콘 컴포넌트로 매핑.
 * @param raw 'pi pi-xxx' / 'pi-xxx' / 'xxx' 형식 모두 허용 (PrimeVue 포팅 잔재 흡수)
 * @returns ICON_MAP 매칭 컴포넌트, 없으면 <AppstoreOutlined /> 폴백
 */
function iconFor(raw?: string): ReactNode {
  if (!raw) return <AppstoreOutlined />
  // 'pi pi-shopping-cart' or 'pi-shopping-cart' or 'shopping-cart'
  const name = raw
    .trim()
    .replace(/^pi\s+/, '')
    .replace(/^pi-/, '')
  return ICON_MAP[name] ?? <AppstoreOutlined />
}

/**
 * MenuNode 트리를 antd Menu items 형태로 재귀 변환.
 * key 우선순위: path > key (라우팅 이동 키와 일치시켜 selectedKeys 매칭 유도).
 */
function toItems(nodes: MenuNode[]): MenuProps['items'] {
  return nodes
    .filter((n) => !n.hidden)
    .map((n) => ({
      key: n.path ?? n.key,
      icon: iconFor(n.icon),
      label: n.label,
      children: n.children?.length ? toItems(n.children) : undefined
    }))
}

// 기본 더미 메뉴 (백엔드 미연결 시) — label 은 렌더 시점에 t() 로 해석
const DEFAULT_MENU: MenuNode[] = [
  { key: 'dashboard', label: 'LBL_KPI_INVENTORY', path: '/dashboard', icon: 'pi-home' },
  {
    key: 'master',
    label: 'LBL_CUSTOMER',
    icon: 'pi-shopping-cart',
    children: [
      { key: 'customer', label: 'LBL_CUSTOMER', path: '/master/customer', icon: 'pi-users' },
      { key: 'product', label: 'COL_PRODUCT_NAME', path: '/master/product', icon: 'pi-box' }
    ]
  }
]

/**
 * AppSidebar — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function AppSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const tree = useMenuStore((s) => s.tree)
  const openTab = useTabStore((s) => s.openTab)
  const themeMode = useThemeStore((s) => s.mode)
  const menuTheme: 'dark' | 'light' = themeMode === 'dark' ? 'dark' : 'light'

  const openKeys = useMenuStore((s) => s.openKeys)
  const setOpenKeys = useMenuStore((s) => s.setOpenKeys)

  const data = tree.length ? tree : DEFAULT_MENU
  const items = useMemo(() => toItems(data), [data])

  // 메뉴 클릭 핸들러
  //  1) 메뉴 트리에서 path 의 라벨을 재귀 탐색 (없으면 path 그대로)
  //  2) navigate(path) 로 라우팅
  //  3) openTab 으로 탭바에도 등록 (closable=true)
  const onClick: MenuProps['onClick'] = (info) => {
    const path = info.key
    const findNode = (nodes: MenuNode[]): MenuNode | null => {
      for (const n of nodes) {
        if ((n.path ?? n.key) === path) return n
        if (n.children) {
          const found = findNode(n.children)
          if (found) return found
        }
      }
      return null
    }
    const node = findNode(data)
    const label = node?.label ?? path
    const labelKey = node?.labelKey
    navigate(path)
    openTab({ key: path, labelKey, label, path, closable: true })
  }

  return (
    <Menu
      theme={menuTheme}
      mode="inline"
      items={items}
      selectedKeys={[location.pathname]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys as string[])}
      onClick={onClick}
    />
  )
}
